import React from 'react';
import { once } from 'lodash';
import { createBrowserHistory } from 'history';
import { SWRConfig } from 'swr';
import { ConnectedRouter } from 'connected-react-router';
import { hydrate, unmountComponentAtNode } from 'react-dom';
import Loadable from 'react-loadable';
import { hot } from 'react-hot-loader';
import { Provider } from 'react-redux';
import { CookiesProvider } from 'react-cookie';
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import { parse } from 'query-string';

import {
  createSplitFilter,
  setWavetrakFeatureFrameworkCB,
  setWavetrakSplitFilters,
  getWavetrakSplitFilters,
  getUserDetails,
  getWavetrakIdentity,
} from '@surfline/web-common';

import configureStore from '../stores';
import * as treatments from '../common/treatments';
import config from '../config';

import '../styles/base.scss';
import setNewRelicErrorHandler from '../utils/setNewRelicErrorHandler';

const TWENTY_MIN_DEDUPE_INTERVAL = 1200000;
const root = document.querySelector('#root');
const preloadedState = window.__DATA__; // eslint-disable-line no-underscore-dangle

// Create a history of your choosing (we're using a browser history in this case)
const history = createBrowserHistory();

const store = configureStore(preloadedState, history);

window.wavetrakStore = store;

setNewRelicErrorHandler();

const setupLogRocket = (query) => {
  const { anonymousId } = getWavetrakIdentity() || {};
  const logRocketEnabled = query?.logrocket === 'on';

  if (logRocketEnabled) {
    LogRocket.init(config.appKeys.logrocket, {
      network: {
        requestSanitizer: (req) => {
          req.headers['X-Auth-AccessToken'] = 'redacted';
          req.url = req.url.replace(/access(_)?token=([^&]*)/, 'accesstoken=**redacted**');
          req.url = req.url.replace(/email=([^&]*)/, 'email=**redacted**');

          if (req.body) {
            req.body = req.body.replace(/firstName=([^&]*)/, 'firstName=**redacted**');
            req.body = req.body.replace(/lastName=([^&]*)/, 'lastName=**redacted**');
            req.body = req.body.replace(/email=([^&]*)/, 'email=**redacted**');
            req.body = req.body.replace(/password=([^&]*)/, 'password=**redacted**');
            req.body = req.body.replace(/access(_)?token=([^&]*)/, 'accesstoken=**redacted**');
          }

          return req;
        },

        responseSanitizer: (res) => {
          if (res.body) {
            res.body = res.body.replace(/firstName=([^&]*)/, 'firstName=**redacted**');
            res.body = res.body.replace(/lastName=([^&]*)/, 'lastName=**redacted**');
            res.body = res.body.replace(/email=([^&]*)/, 'email=**redacted**');
            res.body = res.body.replace(/password=([^&]*)/, 'password=**redacted**');
            res.body = res.body.replace(/access(_)?token=([^&]*)/, 'accesstoken=**redacted**');
          }

          return res;
        },
      },
    });

    setupLogRocketReact(LogRocket);

    const storeState = store.getState();
    const userData = getUserDetails(storeState);

    if (userData) {
      LogRocket.identify(userData?._id || anonymousId || 'Unidentified');
    }
  }
};

const doHydrate = () => {
  // eslint-disable-next-line global-require,import/newline-after-import
  const Routes = hot(module)(require('../components/Routes').default);

  Loadable.preloadReady().then(() => {
    hydrate(
      <CookiesProvider>
        <Provider store={store}>
          <ConnectedRouter history={history}>
            <SWRConfig
              value={{
                shouldRetryOnError: false,
                revalidateOnFocus: false,
                dedupingInterval: TWENTY_MIN_DEDUPE_INTERVAL,
              }}
            >
              <React.StrictMode>
                <Routes />
              </React.StrictMode>
            </SWRConfig>
          </ConnectedRouter>
        </Provider>
      </CookiesProvider>,
      root
    );
  });
};

setWavetrakFeatureFrameworkCB(
  once(() => {
    setupLogRocket(parse(window?.location?.search));
    doHydrate();
  })
);

const mount = async () => {
  try {
    // Get the list on enabled splits and create the list of treatment names to load
    const splitFilters = createSplitFilter(treatments);

    // Set the treatment names on the window so that backplane can access them
    setWavetrakSplitFilters([...splitFilters, ...getWavetrakSplitFilters()]);
  } catch (error) {
    window.newrelic?.noticeError(error);
  }
};

mount();

if (module.hot) {
  module.hot.accept('../components/Routes', () => {
    unmountComponentAtNode(root);
    doHydrate();
  });
}
